import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { cn } from "@/lib/utils";
import fr from "@/locales/fr/common.json";
import nl from "@/locales/nl/common.json";
import en from "@/locales/en/common.json";
import { FormField } from "@/components/ui/FormField";

type Lang = "fr" | "nl" | "en";

/**
 * Aplatit un objet imbriqué : { common: { save: "Enregistrer" } } →
 * { "common.save": "Enregistrer" }. Garde uniquement les valeurs string.
 */
function flatten(obj: Record<string, unknown>, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") {
      out[fullKey] = v;
    } else if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flatten(v as Record<string, unknown>, fullKey));
    }
  }
  return out;
}

/**
 * Vue read-only de la matrice des traductions FR/NL/EN.
 *
 * Pour activer l'édition (3 colonnes editables persistées en DB), il faudra
 * une table `translations(key, fr, nl, en)` + un override des resources i18n
 * au boot pour merger les valeurs DB sur les valeurs JSON. Hors-scope ici.
 */
export function TraductionsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const flat: Record<Lang, Record<string, string>> = {
      fr: flatten(fr as Record<string, unknown>),
      nl: flatten(nl as Record<string, unknown>),
      en: flatten(en as Record<string, unknown>),
    };
    const keys = new Set<string>();
    for (const lang of ["fr", "nl", "en"] as Lang[]) {
      for (const k of Object.keys(flat[lang])) keys.add(k);
    }
    return Array.from(keys)
      .sort()
      .map((key) => ({
        key,
        fr: flat.fr[key] ?? "",
        nl: flat.nl[key] ?? "",
        en: flat.en[key] ?? "",
      }));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.key.toLowerCase().includes(q) ||
        r.fr.toLowerCase().includes(q) ||
        r.nl.toLowerCase().includes(q) ||
        r.en.toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-text-muted" />
          <h1 className="text-lg font-semibold text-text-primary">{t("nav.traductions")}</h1>
          <span className="text-xs text-text-muted">
            {filtered.length === rows.length
              ? `${rows.length} clés`
              : `${filtered.length} / ${rows.length} clés`}
          </span>
        </div>
        <div className="w-full max-w-md">
          <FormField
            label={t("common.search")}
            value={search}
            onChange={setSearch}
            placeholder="..."
          />
        </div>
      </div>

      <p className="text-xs text-text-muted">
        {t("parametres.traductions.readOnlyHint")}
      </p>

      <div className="bg-surface border border-border rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-semibold tracking-widest uppercase text-text-muted w-1/4">
                  {t("parametres.traductions.key")}
                </th>
                <th className="text-left px-4 py-2 text-xs font-semibold tracking-widest uppercase text-text-muted">FR</th>
                <th className="text-left px-4 py-2 text-xs font-semibold tracking-widest uppercase text-text-muted">NL</th>
                <th className="text-left px-4 py-2 text-xs font-semibold tracking-widest uppercase text-text-muted">EN</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-text-muted">
                    {t("common.noResults")}
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => (
                <tr
                  key={row.key}
                  className={cn(
                    "border-b border-border last:border-b-0",
                    i % 2 === 1 && "bg-surface-2/30",
                  )}
                >
                  <td className="px-4 py-2 align-top font-mono text-xs text-text-secondary break-all">
                    {row.key}
                  </td>
                  <td className="px-4 py-2 align-top text-text-primary">{row.fr}</td>
                  <td className="px-4 py-2 align-top text-text-primary">{row.nl}</td>
                  <td className="px-4 py-2 align-top text-text-primary">{row.en}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
